import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import type { DateTime } from "luxon"

import { LocalCache, StorageKey } from "../../../../../shared/cache/LocalCache"
import { DateFilterParams } from "../../../../../shared/components/table/useInitialDateFilters"

import { TimePeriodMeetingService } from "../meetingService/meetingServiceTypes"

type DateRange = Readonly<{
    startDate: DateTime
    endDate: DateTime
}>

function toTimePeriod(value: string | null): TimePeriodMeetingService | undefined {
    return Object.values(TimePeriodMeetingService).find((period) => period === value)
}

export function useProductionPeriodFilter(defaultPeriod: TimePeriodMeetingService) {
    const [searchParams, setSearchParams] = useSearchParams()
    const [isDatePicker, setIsDatePicker] = useState(
        () => searchParams.has(DateFilterParams.START) && searchParams.has(DateFilterParams.END),
    )
    const [timePeriod, setTimePeriod] = useState(
        () => toTimePeriod(LocalCache().get(StorageKey.PRODUCTION_TIME_PERIOD)) ?? defaultPeriod,
    )

    const changeTimePeriod = (period: TimePeriodMeetingService) => {
        setTimePeriod(period)
        LocalCache().set(StorageKey.PRODUCTION_TIME_PERIOD, period)
    }

    const changeDateRangeMode = (nextIsDatePicker: boolean, dateRange: DateRange) => {
        setIsDatePicker(nextIsDatePicker)
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                if (nextIsDatePicker) {
                    next.set(DateFilterParams.START, dateRange.startDate.toISODate() ?? "")
                    next.set(DateFilterParams.END, dateRange.endDate.toISODate() ?? "")
                } else {
                    next.delete(DateFilterParams.START)
                    next.delete(DateFilterParams.END)
                }
                return next
            },
            { replace: true },
        )
    }

    return { isDatePicker, timePeriod, changeTimePeriod, changeDateRangeMode }
}
